import { ref, watch } from 'vue'

/**
 * Les trois façons de dire quand une formation peut avoir lieu, telles que le
 * client les choisit : des jours précis, une période (des mois entiers), ou
 * « je ne sais pas encore ». Elles s'excluent, comme en base
 * (`resolveExclusiveDates`) : un jour posé y effacerait les mois.
 */
export const AVAILABILITY_MODES = Object.freeze({
  DAYS: 'days',
  PERIODS: 'periods',
  UNKNOWN: 'unknown'
})

// Le choix décide de ce qui suit : des cartes, avec leur pictogramme et leur phrase (P5).
export const AVAILABILITY_MODE_OPTIONS = [
  {
    value: AVAILABILITY_MODES.DAYS,
    label: 'J’ai des dates précises',
    description: 'Vous cochez les jours possibles dans le calendrier.',
    icon: 'fas fa-calendar-day',
    testId: 'quote-dates-mode-days'
  },
  {
    value: AVAILABILITY_MODES.PERIODS,
    label: 'J’ai une période en tête',
    description: 'Vous choisissez un ou plusieurs mois.',
    icon: 'fas fa-calendar-alt',
    testId: 'quote-dates-mode-periods'
  },
  {
    value: AVAILABILITY_MODES.UNKNOWN,
    label: 'Je ne connais pas encore les dates',
    description: 'Vous pourrez nous les indiquer plus tard.',
    icon: 'fas fa-question-circle',
    testId: 'quote-dates-inconnues'
  }
]

/** Le choix que racontent des disponibilités déjà saisies ; à défaut, les jours. */
export function availabilityModeOf(state) {
  if (state.dates_inconnues) return AVAILABILITY_MODES.UNKNOWN
  if (state.dates_periodes?.length) return AVAILABILITY_MODES.PERIODS
  return AVAILABILITY_MODES.DAYS
}

/**
 * Le choix courant d'un objet qui porte `dates_disponibilites`,
 * `dates_periodes` et `dates_inconnues` (le formulaire public, la page
 * /calendar). Changer de choix vide les deux autres saisies : sans cela, des
 * jours oubliés partiraient avec les mois et les effaceraient au serveur.
 */
export function useAvailabilityMode(state) {
  const mode = ref(availabilityModeOf(state))

  const setMode = (value) => {
    mode.value = value
    state.dates_inconnues = value === AVAILABILITY_MODES.UNKNOWN
    if (value !== AVAILABILITY_MODES.DAYS) state.dates_disponibilites = []
    if (value !== AVAILABILITY_MODES.PERIODS) state.dates_periodes = []
  }

  // Une saisie changée de l'extérieur (remise à zéro en passant à l'INTER,
  // chargement) ramène le choix sur ce qu'elle dit ; un choix sans saisie
  // encore (« une période », aucun mois coché) reste celui du client.
  watch(
    () => [state.dates_inconnues, state.dates_periodes?.length, state.dates_disponibilites?.length],
    () => {
      const deduit = availabilityModeOf(state)
      const saisi = state.dates_inconnues || state.dates_periodes?.length || state.dates_disponibilites?.length
      if (saisi || mode.value === AVAILABILITY_MODES.UNKNOWN) mode.value = deduit
    }
  )

  return { mode, setMode }
}
